/**
 * Sidebar Component - Global navigation for Yard Jockey
 *
 * Matches Xamarin FlyoutMenuPage design:
 * - Profile section with background image
 * - Avatar overlapping the bottom
 * - Name and phone number
 * - Flat menu list with selected state
 * - Footer with plant name, version, and logout
 */

import { ChevronRight, Globe, Menu } from 'lucide-react';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { MENU_SECTIONS, filterMenuByRole, APP_CONFIG, ROUTES } from '@/constants';
import { supportedLanguages } from '@/i18n';
import { getNotifications } from '@/service/notificationService';
import { useAppSelector, selectUser, selectPlant, selectSettings } from '@/store';
import { cn } from '@/utils/cn';

import type { MenuItem, MenuSection } from '@/constants';

// ─────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────

interface UserAvatarProps {
  firstName: string;
  lastName: string;
  profileImage?: string | undefined;
}

function UserAvatar({ firstName, lastName, profileImage }: UserAvatarProps) {
  const initials = `${firstName ? firstName.charAt(0) : ''} ${lastName ? lastName.charAt(0) : ''}`
    .toUpperCase()
    .trim();

  if (profileImage) {
    return (
      <img
        src={profileImage}
        alt={`${firstName} ${lastName}`}
        className="h-20 w-20 rounded-full object-cover shadow-lg"
      />
    );
  }

  // Blue circle avatar matching Xamarin design (#5582C6) - no border
  return (
    <div className="h-20 w-20 rounded-full bg-[#5582C6] flex items-center justify-center font-normal text-white text-[32px] shadow-lg">
      {initials}
    </div>
  );
}

interface ProfileSectionProps {
  onEditClick?: (() => void) | undefined;
}

function ProfileSection({ onEditClick }: ProfileSectionProps) {
  const { t } = useTranslation();
  const user = useAppSelector(selectUser);

  if (!user) return null;

  const fullName = `${user.first_name} ${user.last_name}`.trim();
  const phoneDisplay = `${user.country_code} ${user.mobile_no}`;

  return (
    <div className="relative">
      {/* Background image section - using Xamarin SVG background */}
      <div
        className="h-28 relative"
        style={{
          backgroundImage: 'url(/images/sidebar-header-bg.svg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        {/* Name, phone, and edit icon - aligned at bottom */}
        <div className="absolute bottom-1 left-[6.5rem] right-1 flex items-end justify-between">
          {/* Name and phone */}
          <div>
            <p className="text-white font-normal text-xs">{fullName}</p>
            <p className="text-white/90 text-xs">{phoneDisplay}</p>
          </div>

          {/* Edit icon - same bottom alignment as name/phone */}
          {onEditClick && (
            <button
              onClick={onEditClick}
              className="p-1 hover:bg-white/20 rounded-full transition-colors"
              aria-label={t('sidebar.editProfile')}
            >
              <img
                src="/images/edit-profile.svg?v=4"
                alt=""
                className="h-6 w-6"
                aria-hidden="true"
              />
            </button>
          )}
        </div>
      </div>

      {/* Avatar - overlapping position */}
      <div className="absolute left-4 bottom-0 translate-y-1/2">
        <UserAvatar
          firstName={user.first_name}
          lastName={user.last_name}
          profileImage={user.profile_image}
        />
      </div>
    </div>
  );
}

interface MenuItemButtonProps {
  item: MenuItem;
  isActive: boolean;
  onClick: () => void;
}

function MenuItemButton({ item, isActive, onClick }: MenuItemButtonProps) {
  const { t } = useTranslation();

  // Use custom SVG icons - white for active, blue for inactive
  const iconSrc = isActive ? item.iconPathWhite : item.iconPath;

  return (
    <button
      onClick={onClick}
      className={cn(
        'w-full flex items-center gap-4 px-6 py-3 transition-all',
        isActive ? 'bg-[#1357A0]' : 'bg-transparent hover:bg-gray-100'
      )}
      aria-current={isActive ? 'page' : undefined}
    >
      <img src={iconSrc} alt="" className="h-6 w-6 flex-shrink-0" aria-hidden="true" />
      <span className={cn('text-sm whitespace-nowrap', isActive ? 'text-white' : 'text-black')}>
        {t(item.labelKey, item.label)}
      </span>
    </button>
  );
}

interface MenuListProps {
  sections: MenuSection[];
  currentRoute: string;
  onItemClick: (item: MenuItem) => void;
}

function MenuList({ sections, currentRoute, onItemClick }: MenuListProps) {
  // Flatten all menu items from all sections
  const allItems = useMemo(() => {
    return sections.flatMap((section) => section.items);
  }, [sections]);

  return (
    <nav className="py-2">
      {allItems.map((item) => (
        <MenuItemButton
          key={item.id}
          item={item}
          isActive={!item.isExternal && currentRoute === item.route}
          onClick={() => {
            onItemClick(item);
          }}
        />
      ))}
    </nav>
  );
}

interface SidebarHeaderProps {
  onClose: () => void;
}

function SidebarHeader({ onClose }: SidebarHeaderProps) {
  const { t } = useTranslation();

  return (
    <div className="h-14 flex items-center px-4 bg-white border-b border-gray-200">
      <button
        onClick={onClose}
        className="p-2 -ml-2 hover:bg-gray-100 rounded-lg transition-colors"
        aria-label={t('sidebar.closeSidebar')}
      >
        <Menu className="h-5 w-5 text-gray-600" />
      </button>
      <span className="ml-2 text-lg font-medium text-gray-800">{t('common.hey', 'Hey')}</span>
    </div>
  );
}

interface FooterSectionProps {
  onLogout: () => void;
  onChangePlant?: (() => void) | undefined;
}

function FooterSection({ onLogout, onChangePlant }: FooterSectionProps) {
  const { t } = useTranslation();
  const plant = useAppSelector(selectPlant);
  const settings = useAppSelector((state) => state.global.settings);
  const env = settings?.ENV ?? '';

  return (
    <div className="p-4 bg-white flex-shrink-0">
      {/* Plant Name Pill */}
      {plant && (
        <button
          onClick={onChangePlant}
          className="w-full mb-4 py-4 px-5 bg-[#D0DBE2] rounded-xl hover:bg-[#c0cdd6] transition-colors"
        >
          <p className="text-[10px] md:text-[12px] lg:text-[14px] text-[#0B2F50] text-center">
            <span className="font-semibold">{t('common.plant', 'Plant: ')}</span>
            <span>{plant.plant_name}</span>
          </p>
        </button>
      )}

      {/* Version and Logout row */}
      <div className="flex items-center justify-between px-1">
        {/* Version */}
        <div className={cn('rounded-lg px-1.5', env === 'dev' && 'bg-red-600')}>
          <span className={cn('text-[10px]', env === 'dev' ? 'text-white' : 'text-black')}>
            {env === 'dev' ? `Dev: V-${APP_CONFIG.version}` : `V-${APP_CONFIG.version}`}
          </span>
        </div>

        {/* Logout */}
        <button
          onClick={onLogout}
          className="text-sm text-black transition-colors"
          aria-label={t('login.logout', 'Logout')}
        >
          {t('login.logout', 'Logout')}
        </button>
      </div>
    </div>
  );
}

interface LanguageButtonProps {
  onClick: () => void;
}

function LanguageButton({ onClick }: LanguageButtonProps) {
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language;
  const currentLangName =
    supportedLanguages.find((l) => l.code === currentLanguage)?.nativeName ?? 'English';

  return (
    <div className="border-t border-gray-200 px-6 py-3">
      <button
        onClick={onClick}
        className="w-full flex items-center justify-between"
        aria-label={t('language.title', 'Language')}
      >
        <div className="flex items-center gap-3">
          <Globe className="h-5 w-5 text-gray-600" aria-hidden="true" />
          <span className="text-sm text-black">{t('language.title', 'Language')}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-[#1357A0] font-medium">{currentLangName}</span>
          <ChevronRight className="h-4 w-4 text-gray-500" />
        </div>
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Main Sidebar Component
// ─────────────────────────────────────────────────────────────────

export interface SidebarProps {
  className?: string;
  expanded: boolean;
  mobileOpen: boolean;
  onToggle: () => void;
  onMobileClose: () => void;
  onEditProfile?: () => void;
  onChangePlant?: () => void;
  onLanguageClick?: () => void;
}

export function Sidebar({
  className,
  expanded,
  mobileOpen,
  onToggle,
  onMobileClose,
  onEditProfile,
  onChangePlant,
  onLanguageClick,
}: SidebarProps) {
  const navigate = useNavigate();
  const location = useLocation();

  const user = useAppSelector(selectUser);
  const plant = useAppSelector(selectPlant);
  const settings = useAppSelector(selectSettings);

  // Get user roles for menu filtering
  const userRoles = useMemo(() => (user?.role ? [user.role] : []), [user?.role]);

  // Filter menu sections based on user roles and config visibility
  // Fallback: if no items match roles, show all items (user is authenticated)
  const visibleSections = useMemo(() => {
    const filtered = filterMenuByRole(MENU_SECTIONS, userRoles);
    // If filtering results in no items, show all menu items as fallback
    const hasItems = filtered.some((section) => section.items.length > 0);
    const roleSections = hasItems ? filtered : MENU_SECTIONS;

    // Apply config-based visibility for Graphical Yard:
    // Show if any config flag is enabled OR if a URL is configured (web app context)
    const mobileEnabled = String(settings?.GRAPHICAL_YARD_MOBILE_ISENABLED) === 'true';
    const showGraphicalYard =
      String(settings?.SHOW_GRAPHICAL_YARD) === 'true' ||
      mobileEnabled ||
      !!settings?.GRAPHICAL_YARD_URL;

    // On screens < 600px (phones), only show if GRAPHICAL_YARD_MOBILE_ISENABLED is true
    const isTabletOrDesktop = window.innerWidth >= 600;

    if (!showGraphicalYard || (!isTabletOrDesktop && !mobileEnabled)) {
      return roleSections.map((section) => ({
        ...section,
        items: section.items.filter((item) => item.id !== 'graphical-yard'),
      }));
    }

    return roleSections;
  }, [userRoles, settings, mobileOpen, expanded]);

  // Handle menu item click — opens external URL in new tab, opens popup, or navigates internally
  const handleMenuItemClick = useCallback(
    (item: MenuItem) => {
      const graphicalYardUrl = settings?.GRAPHICAL_YARD_URL;
      if (item.isExternal && item.id === 'graphical-yard' && graphicalYardUrl) {
        // Matches Xamarin GraphicalYardPageViewModel.cs:36 and FlyoutMenuPageViewModel.cs:359
        // URL template uses {0} = OrgId, {1} = PlantCode
        const url = graphicalYardUrl
          .replace('{0}', String(plant?.org_id ?? ''))
          .replace('{1}', plant?.plant_code ?? '');
        window.open(url, '_blank', 'noopener,noreferrer');
      } else if (item.isExternal && item.id === 'audit-summary') {
        //audit-summary open link as external
        const auditSummaryUrl = settings?.YMS_AUDIT_SUMMARY_URL;
        if (auditSummaryUrl) {
          window.open(auditSummaryUrl, '_blank', 'noopener,noreferrer');
        }
      } else if (item.id === 'yms-dashboard') {
        // Matches Xamarin FlyoutMenuPageViewModel.cs:319-326
        // In Xamarin, YMS_DASHBOARD_WEBVIEW controls in-app WebView vs system browser.
        // In the PWA both cases open the Power BI URL in a new browser tab.
        const ymsDashboardUrl = settings?.YMS_DASHBOARD_URL;
        if (ymsDashboardUrl) {
          window.open(ymsDashboardUrl, '_blank', 'noopener,noreferrer');
        }
      } else {
        if (item.id === 'dashboard') {
          void getNotifications(20, 'All');
        }
        navigate(item.route);
      }
      // Close sidebar on both mobile and desktop after navigation
      onMobileClose();
      onToggle();
    },
    [navigate, onMobileClose, onToggle, settings, plant]
  );

  // Handle logout - close sidebar and navigate to logout summary page
  const handleLogout = useCallback(() => {
    onMobileClose();
    onToggle();
    navigate(ROUTES.logoutSummary);
  }, [navigate, onMobileClose, onToggle]);

  // Sidebar content
  const sidebarContent = (onClose: () => void) => (
    <div className="flex flex-col h-full bg-white">
      {/* Sidebar Header with burger icon and "Hey" text */}
      <SidebarHeader onClose={onClose} />

      {/* Profile Section with background and avatar */}
      <ProfileSection onEditClick={onEditProfile} />

      {/* Spacer for avatar overlap */}
      <div className="h-12 bg-white" />

      {/* Menu List - scrollable */}
      <div className="flex-1 overflow-y-auto bg-white">
        <MenuList
          sections={visibleSections}
          currentRoute={location.pathname}
          onItemClick={handleMenuItemClick}
        />
      </div>

      {/* Language Selector */}
      {onLanguageClick && <LanguageButton onClick={onLanguageClick} />}

      {/* Footer Section with plant, version, logout */}
      <FooterSection onLogout={handleLogout} onChangePlant={onChangePlant} />
    </div>
  );

  return (
    <>
      {/* Desktop Sidebar Overlay - shown when expanded */}
      {expanded && (
        <div
          className="hidden md:block fixed inset-0 z-[60] bg-black/30 transition-opacity duration-300"
          onClick={onToggle}
          aria-hidden="true"
        />
      )}

      {/* Desktop Sidebar - with sliding transition */}
      <aside
        className={cn(
          'hidden md:flex flex-col h-screen fixed top-0 left-0 z-[70]',
          'bg-white shadow-lg border-r border-gray-200',
          'w-72',
          'transform transition-transform duration-300 ease-in-out',
          expanded ? 'translate-x-0' : '-translate-x-full',
          className
        )}
      >
        {sidebarContent(onToggle)}
      </aside>

      {/* Mobile Sidebar Overlay */}
      {mobileOpen && (
        <div
          className="md:hidden fixed inset-0 z-[60] bg-black/50 transition-opacity duration-300"
          onClick={onMobileClose}
          aria-hidden="true"
        />
      )}

      {/* Mobile Sidebar - with sliding transition */}
      <aside
        className={cn(
          'md:hidden fixed inset-y-0 left-0 z-[70]',
          'w-72 bg-white shadow-xl',
          'transform transition-transform duration-300 ease-in-out',
          mobileOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        {sidebarContent(onMobileClose)}
      </aside>
    </>
  );
}

Sidebar.displayName = 'Sidebar';
